import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export type InProgressExercise = {
  attemptId: string;
  exerciseId: string;
  exerciseTitle: string;
  chapterId: string | null;
  chapterTitle: string;
  subject: string;
  level: string;
  chapterSlug: string;
  difficulty: string;
  stepsCompleted: number;
  totalSteps: number;
  startedAt: string;
  illustrationUrl: string | null;
};

type RawAttempt = {
  id: string;
  created_at: string;
  exercise_id: string | null;
};

type RawExercise = {
  id: string;
  title: string;
  difficulty: string | null;
  chapter_id: string | null;
  illustration_url: string | null;
};

type RawChapter = {
  id: string;
  title: string;
  subject: string;
  level: string;
  slug: string;
  illustration_url: string | null;
};

export function useInProgressExercises(studentId: string | undefined) {
  return useQuery({
    queryKey: ['in-progress-exercises', studentId ?? ''],
    enabled: !!studentId,
    staleTime: 1000 * 30,
    queryFn: async (): Promise<InProgressExercise[]> => {
      // Fetch in-progress attempts
      const { data: attempts, error: attemptsError } = await supabase
        .from('attempts')
        .select('id, created_at, exercise_id')
        .eq('student_id', studentId!)
        .eq('status', 'in_progress')
        .order('created_at', { ascending: false })
        .limit(10);

      if (attemptsError) throw attemptsError;
      if (!attempts || attempts.length === 0) return [];

      const rawAttempts = attempts as unknown as RawAttempt[];
      const exerciseIds = rawAttempts.map((a) => a.exercise_id).filter((id): id is string => !!id);
      const attemptIds = rawAttempts.map((a) => a.id);

      if (exerciseIds.length === 0) return [];

      // Exercices + chapitres + nombre de questions V2 (colonne calculée, 0296)
      const { data: exercises, error: exError } = await supabase
        .from('exercises')
        .select(
          'id, title, difficulty, chapter_id, illustration_url, question_count:exercise_question_count, chapters ( id, title, subject, level, slug, illustration_url )',
        )
        .in('id', exerciseIds);

      if (exError) throw exError;

      const rawExercises = exercises as unknown as Array<
        RawExercise & { chapters: RawChapter | null; question_count: number | null }
      >;

      const exerciseMap = new Map(rawExercises.map((e) => [e.id, e]));

      // Fetch attempt_steps en batch pour calculer la progression
      const { data: attemptSteps, error: stepsError } = await supabase
        .from('attempt_steps')
        .select('attempt_id')
        .in('attempt_id', attemptIds);

      if (stepsError) throw stepsError;

      const stepsByAttempt = new Map<string, number>();
      for (const s of (attemptSteps ?? []) as { attempt_id: string }[]) {
        stepsByAttempt.set(s.attempt_id, (stepsByAttempt.get(s.attempt_id) ?? 0) + 1);
      }

      return rawAttempts
        .map((a) => {
          if (!a.exercise_id) return null;
          const ex = exerciseMap.get(a.exercise_id);
          if (!ex) return null;
          const ch = ex.chapters;
          return {
            attemptId: a.id,
            exerciseId: ex.id,
            exerciseTitle: ex.title,
            chapterId: ex.chapter_id ?? null,
            chapterTitle: ch?.title ?? '',
            subject: ch?.subject ?? 'math',
            level: ch?.level ?? '',
            chapterSlug: ch?.slug ?? '',
            difficulty: ex.difficulty ?? 'medium',
            stepsCompleted: stepsByAttempt.get(a.id) ?? 0,
            totalSteps: ex.question_count ?? 0,
            startedAt: a.created_at,
            illustrationUrl: ex.illustration_url ?? ch?.illustration_url ?? null,
          };
        })
        .filter((x): x is InProgressExercise => x !== null);
    },
  });
}
